import { useCallback, useEffect, useEffectEvent, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'

import { selectAppIsLoading, selectCurrentUser, setAppIsLoading } from '@/app/slices/appSlice'
import { selectPlanMetadata } from '@/app/slices/organisationSlice'
import TbxInitializingApp from '@/common/components/TbxLoadingPage/TbxInitializingApp'
import { TbxLoadingPage } from '@/common/components/TbxLoadingPage/TbxLoadingPage'
import {
    AUTH_PROVIDERS,
    getAuthProvider,
    getRegistrationProvider,
    isEmailFirstLoginEnabled,
} from '@/common/helpers/auth/authProviderDetection'
import { getPostLoginReturnTo } from '@/common/helpers/auth/isPublicPath'
import { useAppInitialisation } from '@/common/hooks/useAppInitialisation'
import { useAuth } from '@/common/hooks/useAuth'
import { useErrorRecovery } from '@/common/hooks/useErrorRecovery'
import { Paths } from '@/common/utils'
import Layout from '@/layout/Layout'

import UserObserverContainer from './UserObserverContainer'

/**
 * UserRequiredContainer - Authentication and initialization wrapper for protected routes.
 *
 * Enforces authentication before rendering child routes and handles app initialization.
 * Supports both Azure AD B2C (MSAL) and Kinde authentication providers via the unified
 * useAuth hook.
 *
 * Authentication flow:
 * 1. If auth interaction in progress → show loading
 * 2. If not authenticated:
 *    a. Has login_hint in URL → call login({ login_hint }) for email pre-fill
 *    b. Has cached provider → call login() directly (returning user)
 *    c. No cache → redirect to /login for email-first provider detection
 * 3. If authenticated → run app initialization → render content
 *
 * @returns {JSX.Element} Loading screen, or authenticated content after initialization
 */
const UserRequiredContainer = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const appInsights = useAppInsightsContext()

    // Unified auth hook - supports both MSAL and Kinde providers
    const { isAuthenticated, isInteractionInProgress, login } = useAuth()

    // Call function to get reactive value for useEffect dependency
    const interactionInProgress = isInteractionInProgress()

    const appIsLoading = useSelector(selectAppIsLoading)
    const currentUser = useSelector(selectCurrentUser)
    const planMetadata = useSelector(selectPlanMetadata)

    // Stable trackEvent reference to prevent unnecessary re-renders in child hooks
    const stableTrackEvent = useCallback(
        ({ data, eventName }) => appInsights.trackEvent({ name: eventName, properties: data }),
        [appInsights]
    )

    // Single initialization hook - replaces complex orchestration
    // Uses stable references and primitive values to prevent infinite loops
    const { currentStep, hasDataErrors, initializeApp, isAnyStepLoading, isComplete, steps } = useAppInitialisation({
        currentUser,
        planMetadata,
        location,
        navigate,
        trackEvent: stableTrackEvent,
    })

    // Error recovery functionality
    const { executeAction, resetRetryCounter } = useErrorRecovery()

    // Stable initialization tracking
    const hasInitialized = useRef(false)

    // Guard to prevent double login() calls during state transitions
    const loginInitiated = useRef(false)

    const onInitialize = useEffectEvent(async () => {
        try {
            hasInitialized.current = true
            const result = await initializeApp()

            if (result?.success) {
                dispatch(setAppIsLoading(false))
                resetRetryCounter()
                console.debug('[UserRequiredContainer] User initialization completed:', result?.success)
            } else {
                throw new Error(result.error)
            }
        } catch (error) {
            console.error('[UserRequiredContainer] Initialization failed:', error)
        }
    })

    useEffect(() => {
        // Only initialize when:
        // 1. No auth interaction in progress (not processing redirect callback)
        // 2. User is authenticated
        // 3. Haven't initialized yet
        if (!interactionInProgress && isAuthenticated && !hasInitialized.current) {
            onInitialize()
        }
    }, [interactionInProgress, isAuthenticated])

    // 1. Auth interaction in progress (processing redirect, loading SDK)
    if (interactionInProgress) {
        return <TbxInitializingApp />
    }

    // 2. Not authenticated - handle email-first login flow
    if (!isAuthenticated) {
        // Check for login_hint in URL (set by Login page after provider detection)
        const params = new URLSearchParams(window.location.search)
        const loginHint = params.get('login_hint')

        // Check for cached provider (returning user)
        const cachedProvider = getAuthProvider()

        // Kinde always returns to the configured redirectUri (origin root); it will
        // not exchange a code landed anywhere else, so a deep link can only survive the round
        // trip via the OAuth `state` parameter. `state` collides by name with MSAL's own
        // string CSRF token in the request useAuth merges these options into, so it is added
        // only when the provider about to handle this login is actually Kinde.
        const provider = cachedProvider ?? getRegistrationProvider()
        const returnTo = provider === AUTH_PROVIDERS.KINDE ? getPostLoginReturnTo(location.pathname) : undefined
        const kindeReturnToState = returnTo ? { state: { returnTo } } : undefined

        // Case 1: Email-first flow completed (has login_hint in URL)
        if (loginHint) {
            if (!loginInitiated.current) {
                loginInitiated.current = true
                // MSAL expects 'loginHint' (camelCase), Kinde expects 'login_hint' (snake_case)
                login({ loginHint, login_hint: loginHint, ...kindeReturnToState })
            }
            return <TbxInitializingApp />
        }

        // Case 2: Returning user with cached provider - skip email entry
        if (cachedProvider) {
            if (!loginInitiated.current) {
                loginInitiated.current = true
                kindeReturnToState ? login(kindeReturnToState) : login()
            }
            return <TbxInitializingApp />
        }

        // Case 3: No cached provider
        if (isEmailFirstLoginEnabled()) {
            // Email-first flow enabled: redirect to email entry page
            navigate(Paths.LOGIN_PATHNAME, {
                state: { returnTo: location.pathname + location.search },
                replace: true,
            })
        } else {
            // Email-first flow disabled: go directly to default provider login
            if (!loginInitiated.current) {
                loginInitiated.current = true
                kindeReturnToState ? login(kindeReturnToState) : login()
            }
        }
        return <TbxInitializingApp />
    }

    // 3. Authenticated - render content (initialization handled by useEffect)
    return appIsLoading || isAnyStepLoading || hasDataErrors ? (
        <TbxLoadingPage
            currentStep={currentStep}
            isComplete={isComplete}
            steps={steps}
            onErrorAction={executeAction}
        />
    ) : (
        <UserObserverContainer>
            <Layout>
                <Outlet />
            </Layout>
        </UserObserverContainer>
    )
}

export default UserRequiredContainer
